/**
 * Schema v8 (sync) field groundwork — ISOLATED and DORMANT.
 *
 * Defines the new persisted fields the sync layer needs and a pure,
 * idempotent function to add them to a progress slice WITHOUT changing any
 * existing field.
 *
 * This is intentionally NOT yet wired into `utils/progressMigration.js`: it is
 * built and tested in isolation so that, once the v7→v8 step is approved,
 * wiring it in is a single `ensureSyncFields(out)` call inside that pipeline.
 * Until then the live app never imports this and its boot path is unchanged.
 *
 * New fields (all inert until the sync engine is switched on):
 *   - profileId: pseudonymous learning-profile id (random UUID, no PII).
 *   - deviceId:  per-install id.
 *   - syncMeta:  { baselineUploaded, lastSyncedVersion, lastPulledSeq }.
 *   - outbox:    [] append-only event queue.
 */

import { ensureIdentity } from './identity.js';

export const SYNC_SCHEMA_VERSION = 8;

export const DEFAULT_SYNC_META = Object.freeze({
  baselineUploaded: false,
  lastSyncedVersion: 0,
  lastPulledSeq: 0,
});

/**
 * A fresh set of sync fields, with freshly generated ids unless supplied.
 */
export function defaultSyncFields(identity) {
  const { profileId, deviceId } = ensureIdentity(identity);
  return {
    profileId,
    deviceId,
    syncMeta: { ...DEFAULT_SYNC_META },
    outbox: [],
  };
}

/**
 * Additively ensure the sync fields exist on a progress slice. Pure +
 * idempotent: existing valid values are preserved, missing/invalid ones are
 * filled. Never touches non-sync fields.
 *
 * @param {object} progress - a progress slice (any schema version)
 * @returns {object} a new slice with profileId/deviceId/syncMeta/outbox present
 */
export function ensureSyncFields(progress) {
  const p = progress && typeof progress === 'object' && !Array.isArray(progress) ? progress : {};
  const { profileId, deviceId } = ensureIdentity({
    profileId: p.profileId,
    deviceId: p.deviceId,
  });
  const syncMeta =
    p.syncMeta && typeof p.syncMeta === 'object' && !Array.isArray(p.syncMeta)
      ? { ...DEFAULT_SYNC_META, ...p.syncMeta }
      : { ...DEFAULT_SYNC_META };
  const outbox = Array.isArray(p.outbox) ? p.outbox : [];
  return { ...p, profileId, deviceId, syncMeta, outbox };
}
